import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { CheckCircle, Clock, ArrowsClockwise, Question, Plus, Warning } from "@phosphor-icons/react";

const STATUS_LABEL = {
  deschis: "Deschis", rezolvat: "Rezolvat", amanat: "Amânat",
  clarificare: "Clarificare", redirectionat: "Redirecționat",
};
const STATUS_CLS = {
  deschis: "text-blue-400 ring-blue-500/30", rezolvat: "text-emerald-400 ring-emerald-500/30",
  amanat: "text-amber-400 ring-amber-500/30", clarificare: "text-violet-400 ring-violet-500/30",
  redirectionat: "text-cyan-400 ring-cyan-500/30",
};

export default function TasksPanel({ projectId }) {
  const { hasRole } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assignee_role: "pm", priority: "normal" });
  const [redirectFor, setRedirectFor] = useState(null);
  const [clarifyFor, setClarifyFor] = useState(null);
  const [msg, setMsg] = useState("");
  const [target, setTarget] = useState("");

  const load = () => api.get(`/tasks?project_id=${projectId}`).then((r) => setTasks(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (hasRole("admin")) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, [projectId]);

  const canManage = hasRole("pm", "admin");

  const act = async (id, action, body = {}) => {
    try {
      await api.post(`/tasks/${id}/${action}`, body);
      toast.success("Acțiune aplicată");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const create = async () => {
    if (!form.title.trim()) return toast.error("Titlul e obligatoriu");
    try {
      await api.post("/tasks", { ...form, project_id: projectId });
      toast.success("Task creat și notificat (Bling)");
      setOpen(false);
      setForm({ title: "", description: "", assignee_role: "pm", priority: "normal" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="tasks-panel">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-head text-lg font-bold">Task-uri & Flux automat</h3>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-task-btn"><Plus size={15} className="mr-1" /> Task</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Task nou</DialogTitle></DialogHeader>
              <div className="grid gap-3">
                <div className="grid gap-1.5"><Label>Titlu</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" /></div>
                <div className="grid gap-1.5"><Label>Descriere</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1.5">
                    <Label>Rol responsabil</Label>
                    <Select value={form.assignee_role} onValueChange={(v) => setForm({ ...form, assignee_role: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["vanzari", "pm", "inginer"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-1.5">
                    <Label>Prioritate</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["low", "normal", "high", "urgent"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="save-task-btn">Creează & notifică</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-2">
        {tasks.map((t) => (
          <div key={t.id} className="rounded-lg border border-border p-3" data-testid={`task-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{t.title}</span>
                  {t.overdue && <Warning size={15} weight="fill" className="text-red-400" title="Termen depășit" />}
                  {t.escalation_level > 0 && (
                    <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-bold text-red-400">
                      Escaladat N{t.escalation_level}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Termen: {fmtDate(t.due_date)} · Rol: {t.assignee_role}
                </div>
              </div>
              <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ring-1", STATUS_CLS[t.status])}>
                {STATUS_LABEL[t.status]}
              </span>
            </div>
            {t.status !== "rezolvat" && (
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act(t.id, "resolve")} data-testid={`resolve-${t.id}`}>
                  <CheckCircle size={13} className="mr-1 text-emerald-400" /> Rezolvat
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act(t.id, "snooze", { days: 2 })} data-testid={`snooze-${t.id}`}>
                  <Clock size={13} className="mr-1 text-amber-400" /> Amânare
                </Button>
                {canManage && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setRedirectFor(t); setTarget(""); }}>
                    <ArrowsClockwise size={13} className="mr-1 text-cyan-400" /> Redirecționează
                  </Button>
                )}
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setClarifyFor(t); setMsg(""); }}>
                  <Question size={13} className="mr-1 text-violet-400" /> Clarificare
                </Button>
              </div>
            )}
          </div>
        ))}
        {tasks.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Niciun task. La avansarea etapelor din flux, task-urile se creează automat pentru rolul responsabil.
          </p>
        )}
      </div>

      {/* redirect dialog */}
      <Dialog open={!!redirectFor} onOpenChange={(v) => !v && setRedirectFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Redirecționează task</DialogTitle></DialogHeader>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger data-testid="redirect-target"><SelectValue placeholder="Alege utilizator" /></SelectTrigger>
            <SelectContent>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button disabled={!target} onClick={() => { act(redirectFor.id, "redirect", { assignee_id: target }); setRedirectFor(null); }}>
              Redirecționează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* clarify dialog */}
      <Dialog open={!!clarifyFor} onOpenChange={(v) => !v && setClarifyFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cere clarificare</DialogTitle></DialogHeader>
          <Input placeholder="Ce clarificare ai nevoie?" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="clarify-msg" />
          <DialogFooter>
            <Button onClick={() => { act(clarifyFor.id, "clarify", { message: msg }); setClarifyFor(null); }}>Trimite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
